var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { LockStatus, RetryStatus } from '@common/contracts';
import { getNodeStatuses } from '../../api/node-statuses';
import ImageDropzone from '../ImageDropzone/ImageDropzone';
export function NodeStatusModalForm({ isOpen, onClose, onSubmit, isLoading, mode, nodeStatus, title, submitIdleLabel, submitBusyLabel, bindingSelectId }) {
    const [name, setName] = useState('');
    const [image, setImage] = useState(null);
    const [selectedBinding, setSelectedBinding] = useState(''); // Combined binding state
    const [nameError, setNameError] = useState('');
    const [bindingError, setBindingError] = useState('');
    const [availableStatuses, setAvailableStatuses] = useState([]);
    // Reset (create) or populate (edit) the form state every time the modal opens
    useEffect(() => {
        if (!isOpen)
            return;
        if (mode === 'edit') {
            if (!nodeStatus)
                return;
            setName(nodeStatus.name);
            setImage(nodeStatus.image);
            // Set combined binding from either initial lock or retry binding
            setSelectedBinding(nodeStatus.initialLockBinding || nodeStatus.allowRetryBinding || '');
        }
        else {
            setName('');
            setImage(null);
            setSelectedBinding('');
        }
        setNameError('');
        setBindingError('');
    }, [isOpen, nodeStatus, mode]);
    // Load available statuses to check bindings
    useEffect(() => {
        if (isOpen) {
            const loadStatuses = () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const statuses = yield getNodeStatuses();
                    setAvailableStatuses(statuses);
                }
                catch (error) {
                    console.error('Error loading node statuses:', error);
                }
            });
            loadStatuses();
        }
    }, [isOpen]);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim()) {
            setNameError('Name is required');
            return;
        }
        // Check if the selected binding is already used by another status
        // (in edit mode the currently edited status is excluded from the check)
        if (selectedBinding) {
            const isUsed = availableStatuses.some(status => status.id !== (nodeStatus === null || nodeStatus === void 0 ? void 0 : nodeStatus.id) &&
                (status.initialLockBinding === selectedBinding || status.allowRetryBinding === selectedBinding) &&
                !status.isArchived);
            if (isUsed) {
                setBindingError(`Binding "${selectedBinding}" is already bound to another node status`);
                return;
            }
        }
        // Determine which type of binding is selected and set appropriate fields
        let initialLockBinding = null;
        let allowRetryBinding = null;
        if (selectedBinding === LockStatus.Locked || selectedBinding === LockStatus.Unlocked) {
            initialLockBinding = selectedBinding;
        }
        else if (selectedBinding === RetryStatus.Yes || selectedBinding === RetryStatus.No) {
            allowRetryBinding = selectedBinding;
        }
        onSubmit({ name: name.trim(), image, initialLockBinding, allowRetryBinding });
    };
    const handleClose = () => {
        onClose();
    };
    if (!isOpen || (mode === 'edit' && !nodeStatus))
        return null;
    return (_jsx("div", { className: "modal-overlay", onClick: handleClose, children: _jsxs("div", { className: "modal-content", onClick: e => e.stopPropagation(), children: [_jsxs("div", { className: "modal-header", children: [_jsx("h2", { children: title }), _jsx("button", { className: "close-button", onClick: handleClose, disabled: isLoading, children: "\u00D7" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "modal-form", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "nodeStatusName", children: "Name" }), _jsx("input", { id: "nodeStatusName", value: name, onChange: e => { setName(e.target.value); if (nameError)
                                        setNameError(''); }, disabled: isLoading, className: nameError ? 'error' : '' }), nameError && _jsx("span", { className: "error-text", children: nameError })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Image" }), _jsx(ImageDropzone, { currentImageUrl: image, onUploadSuccess: setImage })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: bindingSelectId, children: "Bindings" }), _jsxs("select", { id: bindingSelectId, value: selectedBinding, onChange: (e) => {
                                        setSelectedBinding(e.target.value);
                                        if (bindingError)
                                            setBindingError('');
                                    }, disabled: isLoading, className: bindingError ? 'error' : '', children: [_jsx("option", { value: "", children: "No binding" }), _jsx("option", { value: LockStatus.Locked, children: "Initial Node Status - Locked" }), _jsx("option", { value: LockStatus.Unlocked, children: "Initial Node Status - Unlocked" }), _jsx("option", { value: RetryStatus.Yes, children: "Allow Retry - Yes" }), _jsx("option", { value: RetryStatus.No, children: "Allow Retry - No" })] }), bindingError && _jsx("span", { className: "error-text", children: bindingError })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: handleClose, disabled: isLoading, children: "Cancel" }), _jsx("button", { type: "submit", disabled: isLoading || !name.trim(), children: isLoading ? submitBusyLabel : submitIdleLabel })] })] })] }) }));
}
